"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useRouter } from "next/navigation";
import { useTheme } from "next-themes";
import { useLocale } from "next-intl";
import { locales, localeNames, type Locale } from "@/i18n/config";
import {
  ChevronDown,
  Menu,
  X,
  Sun,
  Moon,
  Languages,
  Check,
  ScanLine,
  FileText,
  Building2,
  Shield,
  Calculator,
  BookOpen,
  Landmark,
  Receipt,
  TrendingUp,
  Armchair,
  Briefcase,
  PiggyBank,
  Scale,
  ShieldCheck,
  Home,
  Wallet,
  Globe,
  GraduationCap,
  Car,
  Gem,
  BadgeIndianRupee,
  ArrowRight,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

/* ─── Logo Wordmark ─── */

/* ─── Icon map for calculator categories ─── */
const ICON_MAP: Record<string, React.ElementType> = {
  Building2,
  Landmark,
  Receipt,
  TrendingUp,
  Armchair,
  BadgeIndianRupee,
  Briefcase,
  Home,
  ShieldCheck,
  Scale,
  PiggyBank,
  Wallet,
  Globe,
  GraduationCap,
  Car,
  Gem,
};

/* Translation-key-based nav data — labels resolved at render time */
const PRODUCT_CHILDREN_KEYS = [
  { tKey: "freeContractScan", descKey: "freeContractScanDesc", href: "/try", icon: ScanLine },
  { tKey: "propertyCheck", descKey: "propertyCheckDesc", href: "/signup", icon: Building2 },
  { tKey: "compareDocuments", descKey: "compareDocumentsDesc", href: "/signup", icon: FileText },
  { tKey: "redFlagDetection", descKey: "redFlagDetectionDesc", href: "/signup", icon: Shield },
];

const POPULAR_CALCULATOR_KEYS = [
  { tKey: "stampDuty", href: "/tools/property/stamp-duty-calculator", icon: Building2 },
  { tKey: "homeLoanEmi", href: "/tools/loans/home-loan-emi", icon: Landmark },
  { tKey: "incomeTax", href: "/tools/tax/income-tax-new-regime", icon: Receipt },
  { tKey: "sipCalculator", href: "/tools/investment/sip-calculator", icon: TrendingUp },
  { tKey: "gratuity", href: "/tools/retirement/gratuity-calculator", icon: Armchair },
  { tKey: "gstCalculator", href: "/tools/tax/gst-calculator", icon: Briefcase },
  { tKey: "fdCalculator", href: "/tools/savings/fd-calculator", icon: PiggyBank },
  { tKey: "hraExemption", href: "/tools/tax/hra-exemption-calculator", icon: Home },
];

const CALCULATOR_CATEGORY_KEYS = [
  { tKey: "catProperty", href: "/tools/property", icon: Building2 },
  { tKey: "catLoans", href: "/tools/loans", icon: Landmark },
  { tKey: "catTax", href: "/tools/tax", icon: Receipt },
  { tKey: "catInvestment", href: "/tools/investment", icon: TrendingUp },
  { tKey: "catRetirement", href: "/tools/retirement", icon: Armchair },
  { tKey: "catSalary", href: "/tools/salary", icon: BadgeIndianRupee },
  { tKey: "catBusiness", href: "/tools/business", icon: Briefcase },
  { tKey: "catSavings", href: "/tools/savings", icon: PiggyBank },
  { tKey: "catInsurance", href: "/tools/insurance", icon: ShieldCheck },
  { tKey: "catLegal", href: "/tools/legal", icon: Scale },
  { tKey: "catPersonalFinance", href: "/tools/personal-finance", icon: Wallet },
  { tKey: "catNri", href: "/tools/nri", icon: Globe },
  { tKey: "catEducation", href: "/tools/education", icon: GraduationCap },
  { tKey: "catVehicle", href: "/tools/vehicle", icon: Car },
  { tKey: "catGold", href: "/tools/gold", icon: Gem },
];

/* ─── Main Header Component ─── */
export function SiteHeader() {
  const pathname = usePathname();
  const router = useRouter();
  const { theme, setTheme } = useTheme();
  const currentLocale = useLocale() as Locale;
  const t = useTranslations("nav");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [langOpen, setLangOpen] = useState(false);
  const [langSwitching, setLangSwitching] = useState(false);
  const dropdownTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const headerRef = useRef<HTMLElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  // Close language dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    if (langOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [langOpen]);

  const handleLangSelect = useCallback(
    async (locale: Locale) => {
      if (locale === currentLocale) { setLangOpen(false); return; }
      setLangSwitching(true);
      try {
        await fetch("/api/locale", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ locale }),
        });
        router.refresh();
        setLangOpen(false);
      } catch { /* silently fail */ }
      finally { setLangSwitching(false); }
    },
    [currentLocale, router]
  );

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [pathname]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setActiveDropdown(null);
      }
    }
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  function handleMouseEnter(label: string) {
    if (dropdownTimeout.current) clearTimeout(dropdownTimeout.current);
    setActiveDropdown(label);
  }

  function handleMouseLeave() {
    dropdownTimeout.current = setTimeout(() => setActiveDropdown(null), 150);
  }

  function isActive(href: string) {
    if (href === "/blog") return pathname.startsWith("/blog");
    if (href === "/tools") return pathname.startsWith("/tools");
    if (href === "/pricing") return pathname === "/pricing";
    return false;
  }

  return (
    <>
    <header
      ref={headerRef}
      className="sticky top-0 z-50 border-b border-border/40 bg-background/85 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 lg:px-6">
        {/* ── Logo ── */}
        <Link href="/" className="flex items-center shrink-0">
          <span className="text-[24px] uppercase tracking-wide leading-none" style={{ fontFamily: 'var(--font-logo)' }}>
            Got<span className="text-brand">RedFlags</span>
          </span>
        </Link>

        {/* ── Desktop Nav ── */}
        <nav className="hidden lg:flex items-center gap-1">
          {/* Products dropdown */}
          <div
            className="relative"
            onMouseEnter={() => handleMouseEnter("Products")}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className={cn(
                "flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                activeDropdown === "Products"
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}
              onClick={() => setActiveDropdown(activeDropdown === "Products" ? null : "Products")}
            >
              {t("products")}
              <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", activeDropdown === "Products" && "rotate-180")} />
            </button>
            {activeDropdown === "Products" && (
              <div
                className="absolute left-0 top-full pt-2"
                onMouseEnter={() => handleMouseEnter("Products")}
                onMouseLeave={handleMouseLeave}
              >
                <div className="w-[340px] rounded-xl border border-border/60 bg-background shadow-xl shadow-black/[0.08] p-2 animate-in fade-in slide-in-from-top-1 duration-200">
                  {PRODUCT_CHILDREN_KEYS.map((child) => {
                    const Icon = child.icon;
                    return (
                      <Link
                        key={child.href}
                        href={child.href}
                        className="flex items-start gap-3 rounded-lg px-3 py-3 transition-colors hover:bg-muted/60 group"
                      >
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted group-hover:bg-brand/10 transition-colors shrink-0 mt-0.5">
                          <Icon className="h-4.5 w-4.5 text-muted-foreground group-hover:text-brand transition-colors" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground/90 group-hover:text-foreground">{t(child.tKey)}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{t(child.descKey)}</p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Calculators mega menu */}
          <div
            className="relative"
            onMouseEnter={() => handleMouseEnter("Calculators")}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className={cn(
                "flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                activeDropdown === "Calculators" || pathname.startsWith("/tools")
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}
              onClick={() => setActiveDropdown(activeDropdown === "Calculators" ? null : "Calculators")}
            >
              {t("calculators")}
              <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", activeDropdown === "Calculators" && "rotate-180")} />
            </button>
            {activeDropdown === "Calculators" && (
              <div
                className="absolute left-1/2 -translate-x-1/2 top-full pt-2"
                onMouseEnter={() => handleMouseEnter("Calculators")}
                onMouseLeave={handleMouseLeave}
              >
                <div className="w-[720px] rounded-xl border border-border/60 bg-background shadow-xl shadow-black/[0.08] p-5 animate-in fade-in slide-in-from-top-1 duration-200">
                  <div className="grid grid-cols-[1fr_1.2fr] gap-6">
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{t("popularCalculators")}</p>
                      <div className="space-y-0.5">
                        {POPULAR_CALCULATOR_KEYS.map((calc) => {
                          const Icon = calc.icon;
                          return (
                            <Link key={calc.href} href={calc.href} className="flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-muted/60 group">
                              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted group-hover:bg-brand/10 transition-colors">
                                <Icon className="h-4 w-4 text-muted-foreground group-hover:text-brand transition-colors" />
                              </div>
                              <span className="text-sm font-medium text-foreground/90 group-hover:text-foreground">{t(calc.tKey)}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{t("allCategories")}</p>
                      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                        {CALCULATOR_CATEGORY_KEYS.map((cat) => {
                          const Icon = cat.icon;
                          return (
                            <Link key={cat.href} href={cat.href} className="flex items-center gap-2 rounded-lg px-2.5 py-2 transition-colors hover:bg-muted/60 group">
                              <Icon className="h-3.5 w-3.5 text-muted-foreground group-hover:text-brand transition-colors shrink-0" />
                              <span className="text-[13px] text-foreground/80 group-hover:text-foreground truncate">{t(cat.tKey)}</span>
                            </Link>
                          );
                        })}
                      </div>
                      <div className="mt-3 pt-3 border-t border-border/40">
                        <Link href="/tools" className="flex items-center gap-2 text-sm font-medium text-brand hover:text-brand/80 transition-colors">
                          {t("viewAllCalculators")}
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Blog */}
          <Link
            href="/blog"
            className={cn(
              "rounded-lg px-3 py-2 text-sm font-medium transition-colors",
              pathname.startsWith("/blog") ? "text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            )}
          >
            {t("blog")}
          </Link>

          {/* Pricing */}
          <Link
            href="/pricing"
            className={cn(
              "rounded-lg px-3 py-2 text-sm font-medium transition-colors",
              pathname === "/pricing" ? "text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            )}
          >
            {t("pricing")}
          </Link>
        </nav>

        {/* ── Desktop CTA ── */}
        <div className="hidden lg:flex items-center gap-2">
          {/* Language selector */}
          <div ref={langRef} className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              disabled={langSwitching}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors disabled:opacity-50"
              aria-label="Change language"
            >
              <Languages className="h-4 w-4" />
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full mt-1 w-48 rounded-xl border border-border/60 bg-background shadow-xl shadow-black/[0.08] overflow-hidden z-50 animate-in fade-in slide-in-from-top-1 duration-200">
                <div className="max-h-[320px] overflow-y-auto py-1">
                  {locales.map((locale) => (
                    <button
                      key={locale}
                      onClick={() => handleLangSelect(locale)}
                      className={cn(
                        "flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors",
                        locale === currentLocale
                          ? "bg-brand/10 text-brand"
                          : "text-foreground/80 hover:bg-muted/60"
                      )}
                    >
                      <span className="text-sm flex-1">{localeNames[locale]}</span>
                      <span className="text-[10px] text-muted-foreground uppercase">{locale}</span>
                      {locale === currentLocale && <Check className="h-3.5 w-3.5 text-brand shrink-0" />}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Theme toggle */}
          <button
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            aria-label="Toggle theme"
          >
            <Sun className="h-4 w-4 hidden dark:block" />
            <Moon className="h-4 w-4 block dark:hidden" />
          </button>

          <div className="w-px h-5 bg-border/60 mx-1" />

          <Link
            href="/try"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {t("freeScan")}
          </Link>
          <Link
            href="/login"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {t("login")}
          </Link>
          <Link
            href="/signup"
            className="inline-flex h-9 items-center rounded-lg bg-brand px-4 text-sm font-medium text-brand-foreground transition-colors hover:bg-brand/90 shadow-sm"
          >
            {t("getStarted")}
          </Link>
        </div>

        {/* ── Mobile: Free Scan + Hamburger ── */}
        <div className="flex lg:hidden items-center gap-2">
          <Link
            href="/try"
            className="inline-flex h-8 items-center rounded-lg bg-brand px-3 text-xs font-medium text-brand-foreground"
          >
            {t("freeScan")}
          </Link>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-foreground hover:bg-muted/50 transition-colors"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
    </header>

    {/* ── Mobile Menu (outside header to avoid backdrop-blur stacking context) ── */}
    {mobileOpen && (
      <div className="lg:hidden fixed inset-x-0 top-[56px] bottom-0 z-[999] bg-background border-t border-border/40 overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            {/* Products section */}
            <MobileAccordion label={t("products")}>
              {PRODUCT_CHILDREN_KEYS.map((child) => {
                const Icon = child.icon;
                return (
                  <Link
                    key={child.href}
                    href={child.href}
                    className="flex items-center gap-3 rounded-lg px-3 py-3 hover:bg-muted/50"
                    onClick={() => setMobileOpen(false)}
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted shrink-0">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{t(child.tKey)}</p>
                      <p className="text-xs text-muted-foreground">{t(child.descKey)}</p>
                    </div>
                  </Link>
                );
              })}
            </MobileAccordion>

            {/* Calculators section */}
            <MobileAccordion label={t("calculators")}>
              <div className="px-3 pb-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  {t("popularCalculators")}
                </p>
                {POPULAR_CALCULATOR_KEYS.map((calc) => {
                  const Icon = calc.icon;
                  return (
                    <Link
                      key={calc.href}
                      href={calc.href}
                      className="flex items-center gap-2.5 rounded-lg py-2.5 hover:bg-muted/50 px-2"
                      onClick={() => setMobileOpen(false)}
                    >
                      {Icon && <Icon className="h-4 w-4 text-muted-foreground shrink-0" />}
                      <span className="text-sm">{t(calc.tKey)}</span>
                    </Link>
                  );
                })}
                <div className="border-t border-border/40 mt-3 pt-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                    {t("allCategories")}
                  </p>
                  <div className="grid grid-cols-2 gap-x-2">
                    {CALCULATOR_CATEGORY_KEYS.map((cat) => {
                      const Icon = cat.icon;
                      return (
                        <Link
                          key={cat.href}
                          href={cat.href}
                          className="flex items-center gap-2 rounded-lg py-2 px-2 hover:bg-muted/50"
                          onClick={() => setMobileOpen(false)}
                        >
                          <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <span className="text-[13px] truncate">{t(cat.tKey)}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
                <Link
                  href="/tools"
                  className="flex items-center gap-2 mt-3 pt-3 border-t border-border/40 text-sm font-medium text-brand"
                  onClick={() => setMobileOpen(false)}
                >
                  {t("viewAllCalculators")}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </MobileAccordion>

            {/* Simple links */}
            <Link
              href="/blog"
              className={cn(
                "flex items-center rounded-lg px-3 py-3 text-sm font-medium transition-colors",
                pathname.startsWith("/blog") ? "text-foreground bg-muted/50" : "text-foreground/80 hover:bg-muted/50"
              )}
              onClick={() => setMobileOpen(false)}
            >
              {t("blog")}
            </Link>
            <Link
              href="/pricing"
              className={cn(
                "flex items-center rounded-lg px-3 py-3 text-sm font-medium transition-colors",
                pathname === "/pricing" ? "text-foreground bg-muted/50" : "text-foreground/80 hover:bg-muted/50"
              )}
              onClick={() => setMobileOpen(false)}
            >
              {t("pricing")}
            </Link>

            {/* Mobile: Language & Theme */}
            <div className="flex items-center gap-2 pt-4 mt-4 border-t border-border/40 px-3">
              {/* Language */}
              <button
                onClick={() => setLangOpen(!langOpen)}
                disabled={langSwitching}
                className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-foreground/80 hover:bg-muted/50 transition-colors flex-1"
              >
                <Languages className="h-4 w-4 shrink-0" />
                <span className="truncate">{localeNames[currentLocale]}</span>
                <ChevronDown className={cn("h-3 w-3 shrink-0 transition-transform", langOpen && "rotate-180")} />
              </button>
              {/* Theme */}
              <button
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border text-foreground/80 hover:bg-muted/50 transition-colors shrink-0"
                aria-label="Toggle theme"
              >
                <Sun className="h-4 w-4 hidden dark:block" />
                <Moon className="h-4 w-4 block dark:hidden" />
              </button>
            </div>
            {langOpen && (
              <div className="mx-3 mt-1 rounded-lg border border-border bg-card overflow-hidden">
                <div className="max-h-[200px] overflow-y-auto py-1">
                  {locales.map((locale) => (
                    <button
                      key={locale}
                      onClick={() => { handleLangSelect(locale); setMobileOpen(false); }}
                      className={cn(
                        "flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors",
                        locale === currentLocale ? "bg-brand/10 text-brand" : "text-foreground/80 hover:bg-muted/60"
                      )}
                    >
                      <span className="text-sm flex-1">{localeNames[locale]}</span>
                      <span className="text-[10px] text-muted-foreground uppercase">{locale}</span>
                      {locale === currentLocale && <Check className="h-3.5 w-3.5 text-brand shrink-0" />}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Mobile CTA */}
            <div className="pt-3 mt-3 border-t border-border/40 space-y-2">
              <Link
                href="/try"
                className="flex h-11 items-center justify-center rounded-lg bg-brand text-sm font-medium text-brand-foreground"
                onClick={() => setMobileOpen(false)}
              >
                {t("tryScan")}
              </Link>
              <div className="flex gap-2">
                <Link
                  href="/login"
                  className="flex-1 flex h-10 items-center justify-center rounded-lg border border-border text-sm font-medium text-foreground/80 hover:bg-muted/50"
                  onClick={() => setMobileOpen(false)}
                >
                  {t("login")}
                </Link>
                <Link
                  href="/signup"
                  className="flex-1 flex h-10 items-center justify-center rounded-lg bg-foreground text-sm font-medium text-background"
                  onClick={() => setMobileOpen(false)}
                >
                  {t("signUp")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ─── Mobile Accordion ─── */
function MobileAccordion({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between rounded-lg px-3 py-3 text-sm font-medium text-foreground/80 hover:bg-muted/50 transition-colors"
      >
        {label}
        <ChevronDown
          className={cn(
            "h-4 w-4 text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
        />
      </button>
      {open && <div className="pb-2">{children}</div>}
    </div>
  );
}
